import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class FontService {
  private loadedFonts: Set<string> = new Set();
  private customFontLoaded: boolean = false;

  private fontUrls: Record<string, string> = {
    "source-serif": 'https://fonts.googleapis.com/css2?family=Source+Serif+4:opsz,wght@8..60,200..900&display=swap',
    "outfit": 'https://fonts.googleapis.com/css2?family=Outfit:wght@100..900&display=swap',
    "inter": 'https://fonts.googleapis.com/css2?family=Inter:wght@100..900&display=swap',
  }

  private fontFamilyNames: Record<string, string> = {
    "source-serif": "Source Serif 4",
    "outfit": "Outfit",
    "inter": "Inter"
  }

  private fontFallbacks: Record<string, string> = {
    "source-serif": "serif",
    "outfit": "sans-serif",
    "inter": "sans-serif"
  }

  // TODO THIS IS NOT WORKING, CHECK tailwind.config.js
  private fontScaleFactors: Record<string, number> = {
    "source-serif": 1.2,    // 20% larger to match x-height of others
    "outfit": 1,            // baseline size
    "inter": 1              // baseline size
  }

  loadFont(fontKey: string): Promise<void> {
    // Default to outfit font if not found
    if (!this.fontUrls[fontKey]) {
      fontKey = 'outfit';
    }

    if (this.loadedFonts.has(fontKey)) {
      return Promise.resolve();
    }

    const link = document.createElement('link');
    link.href = this.fontUrls[fontKey];
    link.rel = 'stylesheet';

    return new Promise((resolve, reject) => {
      console.log(`Loading font: ${fontKey} from ${link.href}`);
      link.onload = () => {
        this.loadedFonts.add(fontKey);
        console.log(`Font loaded: ${fontKey}`);
        const fontName = this.fontFamilyNames[fontKey];
        // Quote font name if it contains spaces
        const quotedFontName = fontName.includes(' ') ? `"${fontName}"` : fontName;
        document.documentElement.style.setProperty(
          '--theme-font-family',
          `${quotedFontName}, ${this.fontFallbacks[fontKey]}`
        );
        document.documentElement.style.setProperty(
          '--theme-font-scale',
          this.fontScaleFactors[fontKey].toString()
        );
        resolve();
      };
      link.onerror = () => {
        console.log(`Failed to load font: ${fontKey}`);
        reject(`Failed to load font: ${fontKey}`);
      };
      document.head.appendChild(link);
    });
  }

  unloadFont(fontKey: string): void {
    const links = document.getElementsByTagName('link');
    for (let i = 0; i < links.length; i++) {
      if (links[i].href === this.fontUrls[fontKey]) {
        links[i].remove();
        this.loadedFonts.delete(fontKey);
        break;
      }
    }
  }

  loadCustomFont(fontUrl: string): Promise<void> {
    if (this.customFontLoaded) {
      this.unloadCustomFont();
    }

    // Generate a unique font family name
    const fontFamilyName = 'CustomFont';  // We'll use a fixed name instead of the filename
    console.log(`Loading custom font from ${fontUrl} as ${fontFamilyName}`);
    // Create and inject @font-face rule
    const style = document.createElement('style');
    style.textContent = `
      @font-face {
        font-family: '${fontFamilyName}';
        src: url('${fontUrl}') format('woff2');
        font-display: swap;
      }
    `;
    document.head.appendChild(style);

    // Load the font file
    return new Promise((resolve, reject) => {
      const font = new FontFace(fontFamilyName, `url(${fontUrl})`);

      font.load().then(() => {
        console.log(`Custom font loaded: ${fontFamilyName}`);
        // @ts-ignore
        document.fonts.add(font);
        this.customFontLoaded = true;

        // Set the CSS variable with the proper font family name
        document.documentElement.style.setProperty(
          '--theme-secondary-font-family',
          `${fontFamilyName}, var(--theme-font-family)`
        );
        resolve();
      }).catch((error) => {
        console.log(`Failed to load custom font: ${error}`);
        reject(`Failed to load custom font: ${error}`);
      });
    });
  }

  unloadCustomFont(): void {
    // Remove @font-face style
    const styles = document.getElementsByTagName('style');
    for (let i = 0; i < styles.length; i++) {
      // @ts-ignore
      if (styles[i].textContent.includes('@font-face') && styles[i].textContent.includes('CustomFont')) {
        styles[i].remove();
        break;
      }
    }

    this.customFontLoaded = false;
    document.documentElement.style.removeProperty('--theme-secondary-font-family');
  }

  private extractFontFamily(fontUrl: string): string {
    // This is a simple extraction - you might want to enhance this based on your URL format
    const urlParts = fontUrl.split('/');
    const fontPart = urlParts[urlParts.length - 1].split('?')[0];
    return fontPart.split('+').join(' ');
  }
}
